import { useInfiniteQuery } from '@tanstack/react-query';

import { artiaProtectedApi } from '@/lib/api-client';

import { TherapeuticClassReportItem, TherapeuticClassReportParams, TherapeuticClassReportResponse } from './use-therapeutic-class-report';

// Custom params for infinite loading
export interface InfiniteTherapeuticClassReportParams extends Omit<TherapeuticClassReportParams, 'page'> {
  pageSize?: number; // How many items to load per page
}

const fetchTherapeuticClassReportPage = async (
  params: InfiniteTherapeuticClassReportParams,
  pageParam = 1
): Promise<TherapeuticClassReportResponse> => {
  // Prepare the request body for POST
  const requestBody = {
    classificationId: params.classificationId,
    drugIds: params.drugIds?.length ? params.drugIds : null,
    coverageTagIds: params.coverageTagIds?.length ? params.coverageTagIds : null,
    stateIds: params.stateIds?.length ? params.stateIds : null,
    poolIds: params.poolIds?.length ? params.poolIds : null,
    paTypeIds: params.paTypeIds?.length ? params.paTypeIds : null,
    pdlStatuses: params.pdlStatuses?.length ? params.pdlStatuses : null,
    boardMeetingTypeIds: params.boardMeetingTypeIds?.length ? params.boardMeetingTypeIds : null,
    pagination: {
      page: pageParam,
      resultsPerPage: params.pageSize || 250 // Back to original value
    }
  };

  // Prepare query parameters for sorting
  const queryParams = new URLSearchParams();
  if (params.sortBy) {
    queryParams.append('sortBy', params.sortBy);
  }
  if (params.sortDirection) {
    queryParams.append('sortDirection', params.sortDirection);
  }

  const url = `reports/therapeutic-class${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  try {
    const response = await artiaProtectedApi.post(url, requestBody);
    
    const data = response.data || response;
    
    // Handle the new paginated response structure
    return {
      data: data.reports || [], // Updated to use 'reports' field from API response
      page: data?.page || pageParam,
      resultsPerPage: data?.resultsPerPage || params.pageSize || 250, // Back to original value
      total: data?.total || 0,
      hasNextPage: data?.hasNextPage || false
    };
  } catch (error) {
    console.error('Error fetching therapeutic class report page:', error);
    throw error;
  }
};

/**
 * Hook for fetching therapeutic class report data with infinite scrolling.
 * 
 * This hook handles paginated API requests and automatically combines results
 * from multiple pages into a single dataset. It uses the backend's hasNextPage
 * property to determine when to stop fetching additional pages.
 * 
 * @param params - The query parameters for the therapeutic class report
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns Object containing flattened data, loading states, and pagination controls
 */
export const useInfiniteTherapeuticClassReport = (
  params: InfiniteTherapeuticClassReportParams,
  enabled = true
) => {
  const query = useInfiniteQuery({
    queryKey: ['therapeutic-class-report-infinite', params],
    queryFn: ({ pageParam = 1 }) => fetchTherapeuticClassReportPage(params, pageParam as number),
    initialPageParam: 1,
    getNextPageParam: (lastPage: TherapeuticClassReportResponse, allPages) => {
      // Guard against invalid data
      if (!lastPage || !allPages || allPages.length === 0) {
        return undefined;
      }

      // Use the backend's hasNextPage property if available (preferred method)
      if (lastPage.hasNextPage !== undefined) {
        return lastPage.hasNextPage ? allPages.length + 1 : undefined;
      }
      
      // Fallback: If we have pagination info, use it
      if (lastPage.total && lastPage.resultsPerPage && lastPage.total > 0) {
        const totalPages = Math.ceil(lastPage.total / lastPage.resultsPerPage);
        const currentPage = lastPage.page || allPages.length;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      }
      
      // Final fallback: if the last page has data equal to page size, there might be more
      const pageSize = params.pageSize || 250; // Back to original value
      if (lastPage.data && Array.isArray(lastPage.data) && lastPage.data.length === pageSize) {
        return allPages.length + 1;
      }
      
      return undefined;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    // Add a smaller gcTime to prevent stale sorting data
    gcTime: 1 * 60 * 1000, // 1 minute
    // Ensure component re-renders when sorting changes
    notifyOnChangeProps: ['data', 'isLoading', 'isFetching', 'error'],
  });

  // Flatten all pages into a single array
  const allData: TherapeuticClassReportItem[] = query.data?.pages?.flatMap((page: TherapeuticClassReportResponse) => page.data || []) || [];
  
  // Get summary from the first page (it should be the same across all pages)
  const summary = query.data?.pages?.[0]?.summary;
  
  // Calculate total count
  const totalCount = query.data?.pages?.[0]?.total || allData.length;
  
  return {
    data: allData,
    summary,
    totalCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch
  };
};
